import { DASHBOARD_STORE_KEYS } from "./constants";

export interface TopEmployeesDto{
  dateStart: string | null;
  dateEnd: string | null;
  isPeriod: boolean;
  typeId: number;
  departmentId?: number;
}

export interface CompanyPlanResultDto{
  dateStart: string | null;
  dateEnd: string | null;
  type: MainTypesPlans;
  isPeriod: boolean;
}

/** Разрешённые ID отделов для плана отделов (v2 departmentsResults), в т.ч. для директора */
/** Подразделение 74 игнорируется */
/** Порядок карточек на дашборде директора (ОЭК и СТМ поменяны местами по запросу) */
export const DEPARTMENTS_PLAN_IDS = [5, 76, 91, 149, 109, 94] as const;

export interface DepartmentsResultDto {
  dateStart: string;
  dateEnd: string;
  isPeriod: boolean;
  type: MainTypesPlans;
  departmentId: number;
}

export interface DepartmentPlanDeal {
  id: number;
  link: string;
  invoice_date: string;
  assigned_by_id: number;
  opportunity: number;
  profit: number;
  /** Прибыль для маржи */
  margin_profit?: number;
  /** Маржинальность, % */
  marginality?: number;
  actual_paid: number;
  stage_id: string;
  responsible: string;
  /** Название компании по ИНН сделки */
  client?: string;
}

export interface DealsAnalyticsContext {
  dateStart: string;
  dateEnd: string;
  scopeLabel: string;
  opportunityTarget: number;
  profitTarget: number;
  opportunityPreparation?: number;
  profitPreparation?: number;
  plannedSalesOpportunity?: number;
  plannedSalesProfit?: number;
  /** Нужны для загрузки рабочей воронки (КП → счёт → к отгрузке). */
  departmentId?: number;
  userId?: number;
  scopeKind?: 'company' | 'department' | 'employee';
}

export interface PipelineDeal {
  id: number;
  link: string;
  estimated_shipment_date: string | null;
  assigned_by_id: number;
  opportunity: number;
  profit: number;
  margin_profit: number;
  marginality: number;
  actual_paid: number;
  stage_id: string;
  stage_group: 'proposal' | 'invoice' | 'preparation';
  responsible: string;
  client: string;
}

export interface ProductAnalyticsItem {
  name: string;
  revenue: number;
  quantity: number;
  rows: number;
  deals: number;
}

export interface DealProductsAnalytics {
  coverage: { requestedDeals: number; dealsWithProducts: number; productRows: number };
  totalRevenue: number;
  categories: ProductAnalyticsItem[];
  products: Array<ProductAnalyticsItem & { category: string }>;
  priceSignals: Array<{
    dealId: number;
    dealLink: string;
    productName: string;
    category: string;
    price: number;
    benchmarkPrice: number;
    deviationPct: number;
    quantity: number;
    impact: number;
    kind: 'discount' | 'premium';
    explicitDiscountRate: number;
  }>;
  dealPricing: Array<{
    dealId: number;
    dealLink: string;
    discountImpact: number;
    premiumImpact: number;
    netImpact: number;
    compensationRate: number;
    verdict: 'compensated' | 'underpriced' | 'premium';
    signals: number;
  }>;
}

export interface CompanyResult {
  /** Факт факт — закрытые сделки */
  value: number,
  /** Факт к отгрузке */
  valuePreparation?: number,
  /** Суммарный факт: value + valuePreparation */
  totalFact?: number,
  target: number,
  precent: number,
  /** Сумма, заплаченная из факта выполнения */
  paid?: number,
  /** Факт за предыдущий период */
  lastPeriod?: { value: number },
  /** Тренд на конец текущего периода */
  trend?: { value: number },
  /** Плановые продажи, сумма (сделки в работе по плановой дате отгрузки) */
  plannedSalesOpportunity?: number,
  /** Плановые продажи, прибыль */
  plannedSalesProfit?: number,
  /** Детализация по сделкам (для плана отделов) */
  deals?: DepartmentPlanDeal[]
}

export enum MainTypesPlans {
  profit = 'profit',
  opportunitie = 'opportunitie'
}

/** v1: с plans */
export interface TopDepartmentResultInterface {
  name: string;
  plans?: MyPlanResultInterface;
  /** v2: плоская структура */
  value?: number;
  target?: number;
  precent?: number;
  paid?: number;
}

export interface TopEmployeesResult {
  id: number;
  place: number;
  percent: number;
}

export interface TopDepartmentsResult {
  id: number;
  name: string;
  place: number;
  percent: number;
}

export interface LtwCompanyResultItem {
  inn: string;
  total: number;
  place: number;
  name: string;
}

export interface TopDepartmentsDto extends MyPlanResultDto {
  typeId?: number;
  /** v2: "profit" | "opportunitie" */
  type?: MainTypesPlans;
}

export interface MyPlanResultDto {
  dateStart: string | null;
  dateEnd: string | null;
  isPeriod: boolean;
  /** ID отдела сотрудника (для личного плана — отдел из user.bx.department) */
  departmentId?: number;
}
export interface UserPlanResultDto extends MyPlanResultDto{
  userId: number
  /** ID отдела для контекста плана пользователя */
  departmentId?: number
}
export interface DeparrtmentPlanResultDto extends MyPlanResultDto{
  departmentId: number
}
export interface MainPlanResultInterface extends CompanyResult {}
export interface SecondPlanResultInterface extends CompanyResult {
  code: string
}

export interface MyPlanResultInterface {
  profit: MainPlanResultInterface;
  opportunity: MainPlanResultInterface;
  second: SecondPlanResultInterface[];
  /** Плановые продажи, сумма */
  plannedSalesOpportunity?: number;
  /** Плановые продажи, прибыль */
  plannedSalesProfit?: number;
  /** Детализация по сделкам (v2 myPlanResult/userPlanResult возвращают deals в корне) */
  deals?: DepartmentPlanDeal[];
}
export interface DepartmentPlanResultInterface extends MyPlanResultInterface {}

export type DashboardStoreKey = typeof DASHBOARD_STORE_KEYS[number];

export interface DashboardStoreInterface{
  loading: Record<DashboardStoreKey, boolean>;
  setLoadingState: (key: DashboardStoreKey, v: boolean) => void;

  error: Record<DashboardStoreKey, string | null>;
  setErrorState: (key: DashboardStoreKey, e: string | null) => void;


  dateStart: string | null;
  setDateStart: (date: string) => void;

  dateEnd: string | null;
  setDateEnd: (date: string) => void;

  selectedDepartment: string | null;
  setSelectedDepartment: (department: string | null) => void;


  companyPlanResultProfit: CompanyResult | null;
  setCompanyPlanResultProfit: (value: CompanyResult | null) => void;
  companyPlanResultOpportunity: CompanyResult | null;
  setCompanyPlanResultOpportunity: (value: CompanyResult | null) => void;
  
  dashboardTopEmployeesCompany: TopEmployeesResult[];
  setDashboardTopEmployeesCompany: (topEmployees: TopEmployeesResult[]) => void;
  dashboardTopEmployeesDepartment: TopEmployeesResult[];
  setDashboardTopEmployeesDepartment: (topEmployees: TopEmployeesResult[]) => void;
  
  myPlanResult: MyPlanResultInterface | null;
  setMyPlanResult: (p: MyPlanResultInterface | null) => void;
  userPlanResult: MyPlanResultInterface | null;
  setUserPlanResult: (p: MyPlanResultInterface | null) => void;
  departmentPlanResult: DepartmentPlanResultInterface | null;
  setDepartmentPlanResult: (p: DepartmentPlanResultInterface | null) => void;
  dashboardTopDepartments: TopDepartmentResultInterface[];
  setDashboardTopDepartments: (topDepartments: TopDepartmentResultInterface[]) => void;




  dashboardLTWCompany: LtwCompanyResultItem[];
  setDashboardLTWCompany: (lTWCompany: LtwCompanyResultItem[]) => void;
}
